/**
 * "Put me back where I was" after a session ends — the navigation half only.
 *
 * Restoring unsaved editor CONTENT is a separate piece of work and must not be
 * attempted here; this module remembers a route and nothing else.
 *
 * ## Why this is stored on the server
 *
 * The requirement is that signing in again returns the user to their screen
 * *even on a different machine*, which rules out localStorage. It reuses the
 * existing per-user preference store rather than adding a table or an endpoint.
 *
 * ## Why it is deleted as soon as it is used
 *
 * A remembered route can embed a patient MRN — the patient screens are
 * `/patients/:mrn` — so this store can hold a patient identifier against a
 * named user. That is accepted deliberately, on the condition that it is
 * short-lived: the entry is removed the moment it has been consumed, and on
 * every deliberate sign-out, so it survives one interrupted session rather than
 * accumulating. **If you are tempted to drop the delete to simplify a flow,
 * that changes what was agreed, not just the code.** Keep the payload to what
 * navigation needs: no record contents, no labels, no patient name.
 */

import { userPreferencesApi } from '@/api/userPreferencesApi'
import { navPermissionsFor } from '@/lib/permissions'
import { surfaceOf } from '@/lib/viewMode'
import type { AuthSession } from '@/lib/authToken'

export const LAST_LOCATION_KEY = 'session.lastLocation'

export interface LastLocation {
  pathname: string
  /** Query string including the leading `?`, or empty. */
  search: string
  /** The tenant that was selected, so the restored screen resolves the same rows. */
  tenantId: string | null
  /**
   * Which surface recorded it, desktop (`/`) or mobile (`/m/`).
   *
   * Load-bearing, not bookkeeping. The two surfaces are separate routers with
   * different route tables: `/patients/:mrn` exists only on desktop, `/tasks`
   * only on mobile. Restoring a path recorded on the other surface would land
   * on that router's catch-all, so a cross-surface sign-in falls back to the
   * landing page instead.
   */
  surface: '/' | '/m/'
  /** When it was recorded, epoch ms. Diagnostic only. */
  at: number
}

/** Routes never worth returning to, because landing on them is not "where I was". */
const UNRESTORABLE = ['/login', '/reset-password', '/no-access']

export function isRestorablePath(pathname: string): boolean {
  if (!pathname.startsWith('/')) return false
  return !UNRESTORABLE.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function parseLastLocation(value: unknown): LastLocation | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Partial<LastLocation>
  if (typeof v.pathname !== 'string' || !isRestorablePath(v.pathname)) return null
  return {
    pathname: v.pathname,
    search: typeof v.search === 'string' ? v.search : '',
    tenantId: typeof v.tenantId === 'string' ? v.tenantId : null,
    // Anything not explicitly mobile is treated as desktop, so a value written
    // before this field existed still restores on the surface it came from.
    surface: v.surface === '/m/' ? '/m/' : '/',
    at: typeof v.at === 'number' ? v.at : 0,
  }
}

/** The path to navigate to, query string included. */
export function toHref(loc: LastLocation): string {
  return `${loc.pathname}${loc.search}`
}

/**
 * Remember where the user was, before their credentials are cleared.
 *
 * Order matters at the call site: this needs the bearer token, so it has to run
 * before `clearAuth()`. `keepalive` lets the request outlive a page that is
 * already navigating away.
 *
 * Never rejects. A failure here must not stop the sign-out — leaving a session
 * open on an unattended machine to preserve a convenience feature would invert
 * the point of the timeout.
 */
export async function recordLastLocation(
  loc: Omit<LastLocation, 'at'>,
  now: number = Date.now(),
): Promise<void> {
  if (!isRestorablePath(loc.pathname)) return
  try {
    await userPreferencesApi.set(
      LAST_LOCATION_KEY,
      { ...loc, at: now } satisfies LastLocation,
      { keepalive: true },
    )
  } catch {
    // See the docblock: sign-out proceeds regardless.
  }
}

/**
 * Forget it. Called after a restore, on deliberate sign-out, and whenever a
 * stored location turns out to be unusable.
 *
 * Also never rejects, for the same reason — but note the asymmetry with
 * `recordLastLocation`: a failure to WRITE costs a convenience, while a failure
 * to DELETE leaves an identifier behind, so callers should prefer to delete
 * eagerly rather than conditionally.
 */
export async function clearLastLocation(): Promise<void> {
  try {
    await userPreferencesApi.remove(LAST_LOCATION_KEY, { keepalive: true })
  } catch {
    // Best effort; the next successful sign-out or restore clears it.
  }
}

/** Where the user lands after signing in, and why. */
export interface RestoreTarget {
  href: string
  /**
   * Set only when the remembered screen could not be reopened. The login flow
   * shows it once, so the user is not silently dumped somewhere else.
   */
  message?: string
}

export const DEFAULT_LANDING = '/'

/**
 * Non-hook permission check against a cached session.
 *
 * `usePermission` cannot be used at the moment this decision is made: the
 * restore happens inside the sign-in handler, one tick after the session was
 * written and before the provider has re-rendered with it, so the hook would
 * still report the signed-out principal. The rule is the same one it applies —
 * full access wins, otherwise the permission must be held in the tenant being
 * restored, with the shell baseline as a fallback.
 */
function canAccess(pathname: string, session: AuthSession | null, tenantId: string | null): boolean {
  if (!session) return false
  if (session.fullAccess) return true

  const required = navPermissionsFor(pathname)
  // No declared permission means the route is not gated by the nav map. The
  // route guards still apply on arrival; this check exists to avoid sending
  // someone to a screen we already know is closed to them.
  if (required.length === 0) return true

  const held = [
    ...(tenantId ? (session.tenantPermissions?.[tenantId] ?? []) : []),
    ...(session.permissions ?? []),
  ]
  return required.some((key) => held.includes(key))
}

/**
 * Decide where a returning user lands.
 *
 * Returns the default landing page whenever the remembered screen is missing,
 * unusable, or no longer permitted — with a message in the last case, because
 * landing somewhere unexpected with no explanation reads as the feature being
 * broken. Never returns an error route: being unable to restore is a normal
 * outcome, not a failure.
 *
 * A tenant the user has since lost membership of also falls back, since every
 * tenant-scoped request from that screen would 403.
 */
export function resolveRestoreTarget(
  stored: unknown,
  session: AuthSession | null,
  currentSurface: '/' | '/m/' = surfaceOf(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  ),
): RestoreTarget {
  const loc = parseLastLocation(stored)
  if (!loc) return { href: DEFAULT_LANDING }

  // Silent on purpose: signing in on your phone after timing out at a
  // workstation is not a failure, and a message about it would be noise.
  if (loc.surface !== currentSurface) return { href: DEFAULT_LANDING }

  if (loc.tenantId && session && !session.fullAccess) {
    const member = session.tenantIds?.includes(loc.tenantId) ?? false
    if (!member) {
      return {
        href: DEFAULT_LANDING,
        message: 'You no longer have access to the area you were last working in.',
      }
    }
  }

  if (!canAccess(loc.pathname, session, loc.tenantId)) {
    return {
      href: DEFAULT_LANDING,
      message: 'You no longer have permission to open the screen you were last on.',
    }
  }

  return { href: toHref(loc) }
}

/**
 * Read the remembered location, decide where to go, and forget it.
 *
 * The delete is unconditional once something was stored — including when the
 * location turned out to be unusable. A stored route that cannot be restored is
 * exactly the one nobody will clear later, and leaving it behind would keep a
 * patient identifier on file for a screen the user can no longer even open.
 *
 * Not awaited on purpose: the caller is mid-navigation and the user should not
 * wait on a cleanup round-trip. A failure is retried by the next sign-out.
 */
export async function takeRestoreTarget(
  session: AuthSession | null,
  currentSurface?: '/' | '/m/',
): Promise<RestoreTarget> {
  let stored: unknown
  try {
    const all = await userPreferencesApi.getAll()
    stored = all?.[LAST_LOCATION_KEY]
  } catch {
    // No preferences means no restore. Signing in must not fail because of it.
    return { href: DEFAULT_LANDING }
  }

  if (stored === undefined || stored === null) return { href: DEFAULT_LANDING }

  const target = resolveRestoreTarget(stored, session, currentSurface)
  void clearLastLocation()
  return target
}
